import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Search, ArrowRight } from 'lucide-react';

function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC] flex">
      {/* Left side - Hero image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1709684459045-29b79a370117?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxwaXp6YSUyMHNob3AlMjBzdG9yZWZyb250fGVufDB8fHx8MTc3NTg0MDM4NXww&ixlib=rb-4.1.0&q=85"
          alt="Restaurant"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0F2951]/70" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-6">
            <img src="/icon-nav.png" alt="Local Luc" className="h-12 w-12 rounded-xl object-cover" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight mb-4">
            Turn your SEO intention into measurable results
          </h1>
          <p className="text-lg text-white/80 max-w-md">
            Audit, plan, and monitor your local search visibility with actionable intelligence.
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <img src="/icon-nav.png" alt="Local Luc" className="h-12 w-12 rounded-xl object-cover" />
          </div>

          <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0F2951] mb-2">
            Welcome back
          </h2>
          <p className="text-base text-[#64748B] mb-8">
            Sign in to your account to continue
          </p>

          {error && (
            <div data-testid="login-error" className="mb-6 p-4 bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-xl text-[#EF4444] text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Email</Label>
              <Input
                data-testid="login-email-input"
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="border-[#E2E8F0] bg-white rounded-xl focus:ring-2 focus:ring-[#0268C4]/20 h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Password</Label>
              <Input
                data-testid="login-password-input"
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="border-[#E2E8F0] bg-white rounded-xl focus:ring-2 focus:ring-[#0268C4]/20 h-12"
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl font-medium text-base transition-all duration-200 hover:-translate-y-[1px]"
            >
              {loading ? 'Signing in...' : 'Sign in'}
              {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-[#64748B]">
            Don't have an account?{' '}
            <Link data-testid="register-link" to="/register" className="text-[#0268C4] hover:text-[#015BA8] font-medium">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
